import type { Basis, Channel, Component, Flag, FulfilmentComponent, GridRow, LastCommitted, Market, MissingReason, Price, Weighting } from "./api";
import { DEFAULT_BASIS, isNoGrid, PricingError } from "./api";
import type { ProjectionMissing, ProjectionWeighting } from "./projection";

/** The Config gross margin target as C2 v1.7 serves it (`settings.margin_target_pct.value`, a decimal string), null when
 * not set, undefined while the Config read is pending. Nothing in the module holds a target of its own (C1 v1.9). */
export type MarginTarget = string | null | undefined;
export const PAGE_SIZE = 50;
export const ABSENT = "—";

export type Region = "uk" | "eu" | "ae" | "all";
export const REGION_KEYS: Region[] = ["all", "eu", "uk", "ae"];
export const REGIONS: Record<Region, { label: string; markets: Market[] }> = {
  uk: { label: "UK", markets: ["UK"] },
  eu: { label: "EU", markets: ["EU"] },
  ae: { label: "UAE", markets: ["AE"] },
  all: { label: "All", markets: ["UK", "EU", "AE"] },
};
export const MARKET_LABEL: Record<Market, string> = { UK: "UK", EU: "EU", AE: "UAE" };
/** Store names by C3 `channel_location`, as the approved mockup names them. */
export const STORE_LABEL: Record<string, string> = {
  amazon_uk: "Amazon UK", amazon_eu: "Amazon EU", amazon_ae: "Amazon UAE",
  shopify_uk: "Shopify UK", shopify_eu: "Shopify EU", shopify_mena: "Shopify GCC",
};

/** The basis switch (CL-019). A basis with `off` null is live and selectable; the order is the approved toolbar order. */
export const BASES: Array<{ key: string; label: string; off: string | null }> = [
  { key: "current_inventory_cogs", label: "Current Inventory COGS", off: null },
  { key: "last_production_cogs", label: "Last Production COGS", off: null },
  { key: "projected_cogs", label: "Projected COGS", off: "Not yet available. Arrives once Future COGS is built." },
];
/** The label per live basis, derived from BASES so the chip and the basis line can never diverge. */
export const BASIS_LABEL = Object.fromEntries(BASES.filter((b) => b.off === null).map((b) => [b.key, b.label])) as Record<Basis, string>;
/** The `pp_basis` URL value per basis: both selections are explicit, so either survives a reload. */
export const BASIS_URL: Record<Basis, string> = { last_production_cogs: "last_production", current_inventory_cogs: "current_inventory" };
export function basisFromSearch(value: unknown): Basis {
  return value === "current_inventory" || value === "current_inventory_cogs" ? "current_inventory_cogs" : DEFAULT_BASIS;
}
/** The Price Run sort (C1 v1.17, owner ruling Q15). `grid` is C4's order, the default; the URL key is `pp_order` (`pp_sort` is
 * the Orders pane's). Rows are ordered within one market, and a price within one currency, so no figure is compared across
 * currencies (the All-regions rule); Contribution is never a sort key (N4b). */
export type SortKey = "gross_low" | "fulfil_high" | "product" | "price_high";
export const SORTS: Array<{ key: SortKey | "grid"; label: string }> = [
  { key: "grid", label: "Grid order" },
  { key: "gross_low", label: "Gross margin, lowest first" },
  { key: "fulfil_high", label: "Fulfilment share, highest first" },
  { key: "product", label: "Product name" },
  { key: "price_high", label: "Price, highest first (within a currency)" },
];
export function sortFromSearch(value: unknown): SortKey | null {
  return SORTS.some((s) => s.key !== "grid" && s.key === value) ? (value as SortKey) : null;
}
/** Why a measure has no figure in the drawer. `no_currency` is not a row reason: the row has no price or fee currency yet. */
export function measureMissing(reason: MissingReason | "no_currency", currency: string | null): string {
  if (reason === "no_currency") return "Not converted: this row has no price or fee currency yet";
  if (reason === "no_fx_rate") return `${MISSING_LABEL.no_fx_rate} into ${currency ?? "this currency"}`;
  return MISSING_LABEL[reason];
}

/** The two inputs only the projection reads (C2 v1.13): the fee-rate registry's referral and DSF rates for the marketplace. */
export const PROJECTION_MISSING_LABEL: Record<Exclude<ProjectionMissing, MissingReason>, string> = {
  no_referral_rate: "No commission rate in the fee registry",
  no_dsf_rate: "No digital services fee rate in the fee registry",
};
/** The reason a projection figure is missing, in words: the row's reasons as the grid names them, plus the registry's two. */
export function projectionMissing(reason: ProjectionMissing): string {
  return reason in PROJECTION_MISSING_LABEL ? PROJECTION_MISSING_LABEL[reason as keyof typeof PROJECTION_MISSING_LABEL] : MISSING_LABEL[reason as MissingReason];
}

export const MISSING_LABEL: Record<MissingReason, string> = {
  no_price: "No price",
  no_vat_rate: "No VAT rate",
  no_production_record: "No production record",
  no_current_inventory_cost: "No current inventory cost",
  no_fx_rate: "No exchange rate",
  no_referral_cost: "No commission cost",
  no_dsf_cost: "No digital services fee cost",
  no_fba_per_unit_cost: "No FBA fee",
  no_pick_pack_cost: "No pick and pack cost",
  no_packaging_cost: "No packaging cost",
  no_courier_cost: "No courier cost",
  no_other_cost: "Other fees not yet available",
  other_currency_mismatch: "Other fees in another currency",
  no_fulfilment_rows: "No fulfilment rows",
};

/** Every C3 flag. BILLED is the normal case and shows nothing; MISSING is shown through `missing` instead. */
export const FLAG_LABEL: Record<Flag, { text: string; title: string } | null> = {
  BILLED: null,
  ESTIMATE: { text: "Estimate", title: "A standard estimate, not a billed value" },
  PROJECTED: { text: "Projected", title: "A projected value entered ahead of billing" },
  STALE: { text: "Old value", title: "The last value is older than expected" },
  "NO SALES": { text: "No sales yet", title: "No orders yet, so the standard estimate is used" },
  THIN: { text: "Few orders", title: "The value rests on very few orders" },
  CORRECTED: { text: "Corrected", title: "Corrected by hand until a billed value lands" },
  "NO FORWARD PRICE": { text: "No forward price", title: "No forward price exists for this cost" },
  OVERRIDE: { text: "Override", title: "A manual override is in force" },
  INCOMPLETE: { text: "Incomplete", title: "Part of this value is missing" },
  "OFFER EXPIRED": { text: "Offer expired", title: "The supplier offer behind this cost has expired" },
  "AMBIGUOUS SELECTION": { text: "Ambiguous source", title: "More than one source matched, so the choice is ambiguous" },
  "NOT COMPUTED": { text: "Not computed", title: "This value was not computed" },
  "COGS AFTER ORDER": { text: "Cost dated after order", title: "The cost is dated after the order it applies to" },
  MISSING: null,
};

export const COMPONENT_LABEL: Record<Component, string> = {
  referral: "Commission",
  dsf: "Digital services fee",
  fba_per_unit: "FBA fee per unit",
  pick_pack: "Pick and pack",
  packaging: "Packaging",
  courier: "Courier",
  other: "Other fees (3-month trailing)",
};

/** The grid carries no rate, so the notes never state one (owner ruling A2, 2026-09-18). */
export function componentNote(component: Component, row: GridRow): string {
  if (component === "referral") return "a share of the VAT inclusive price · moves with price";
  if (component === "dsf") return "a share of the commission · moves with price";
  if (component === "fba_per_unit") return "size and weight based · fixed";
  if (component === "pick_pack") return "per order line, from billed actuals";
  if (component === "packaging") return "from billed actuals";
  if (component === "other") return "trailing three closed months of the costs that carry no fee name: storage, inbound, subscriptions, adjustments · from Precision Fulfilment Costs";
  return `to ${countryName(row.location)}, from billed actuals`;
}

export const COPY = {
  title: "Price Run",
  note: "Set a new price in the row detail, Save it, then Commit the batch. Committed prices are applied by hand in Seller Central and the Shopify admin until the channel push is built.",
  // C1 v1.17 (chain step N7a): the last commit, the Xero read, the refresh failure, the sort, the All-regions rule and the currency footer.
  lastCommit: (who: string, date: string, n: number) => `${who} · ${date} · ${n} ${n === 1 ? "price" : "prices"}`,
  lastCommitLabel: "Last commit",
  noCommit: "none yet",
  xeroRead: (date: string) => `Xero table read ${date}`,
  xeroReadOld: (date: string) => `Xero table read ${date} · older than expected`,
  xeroNotRead: "Xero table not read yet",
  // On the Current Inventory basis every margin rests on the weekly Xero read (owner ruling A1, N7a review round 1).
  oldXero: (date: string) => `The Xero stock read is from ${date}, older than expected. Current Inventory COGS margins use that read.`,
  noXero: "No Xero stock read yet. Current Inventory COGS margins are missing until it arrives.",
  refreshFailed: (computedAt: string) => `We couldn't refresh the figures. Showing the read from ${computedAt}.`,
  retry: "Retry",
  sortLabel: "Sort",
  allRegionsNote: "Every row reports in its own market currency and is never summed.",
  marketCurrency: (market: string) => `Market currency ${market}`,
  poolCogsFrom: (entity: string) => `Pool COGS from ${entity}`,
  headerLog: (lastCommit: string, fxDate: string, showsEu: boolean) =>
    `Prices from the Price Log, latest commit ${lastCommit} · VAT inclusive · margins on the price without VAT` +
    (showsEu ? ` · PLN and SEK costs converted at the ECB rate of ${fxDate}` : ""),
  header: (imported: string, fxDate: string, showsEu: boolean) =>
    `Prices as imported ${imported} from the legacy price sheet · VAT inclusive · margins on the price without VAT` +
    (showsEu ? ` · PLN and SEK costs converted at the ECB rate of ${fxDate}` : ""),
  basisLine: (basis: Basis, region: string) => `${BASIS_LABEL[basis]} basis · ${region}`,
  late: (computedAt: string) => `The daily calculation is late. Showing the last good figures from ${computedAt}.`,
  lateAfter: (computedAt: string, hours: number) =>
    `The figures are older than the ${hours} hours set in Config. Showing the last good figures from ${computedAt}.`,
  oldFeed: (days: number, latest: string) =>
    `The fulfilment cost feed is ${days} days old (daily feed, last received ${latest}). Figures use the last dated costs.`,
  noFeed: "No fulfilment cost feed has been received yet. Fulfilment figures are missing until it arrives.",
  marketMissing: (market: string) => `No figures yet for ${market}.`,
  emptyTitle: "No figures yet",
  /** The empty state on a non-default basis: the grid on that basis has not been computed yet (the default may well exist). */
  emptyTitleBasis: (basis: Basis) => `No figures on the ${BASIS_LABEL[basis]} basis yet`,
  emptyBodyBasis: "The daily calculation has not written a grid on this basis yet. Switch to Last Production COGS to see today's figures.",
  emptyBody: "The first daily calculation has not run. Figures appear after 08:00 Europe/Berlin.",
  noMatch: "No rows match the filters",
  noRows: "This grid has no rows.",
  onlyMissing: "Only rows with something missing",
  belowTarget: "Below target",
  missing: "Missing",
  // C1 v1.31: a place kept for what is not built says what it waits on, never a release number (every release has shipped).
  notYet: "Not yet",
  competitionLater: "Competitor prices arrive with Market position, once a competitor source is chosen.",
  legendTarget: (target: string) => `Gross margin target ${target}% or above`,
  legendTargetNotSet: (notSet: string | null) => `${notSet ?? "Target not set in Config"}: no row is flagged`,
  legendTargetReading: "Gross margin target: reading Config",
  detailTarget: (target: string) => `GM ${target}% or above`,
  detailTargetReading: "Not read from Config yet",
  legendMissing: "a figure is never shown as zero; the reason is named",
  legendLater: "the place is kept until it is built",
  legendRules: "rules met: the assigned strategy judged by the pricing API at the saved price, else the committed price",
  notConfigured: "The pricing API address is not set on the portal yet. Ask the portal owner.",
  unreachable: "We can't reach the pricing API, so nothing is shown. Try again shortly.",
  forbidden: (areaName: string) => `You do not have access to Precision Pricing. Ask the ${areaName} area owner.`,
  howPricesChange: "Save a new price above, then Commit the batch from the Price Run. A committed price is written to the Price Log and applied by hand in Seller Central or the Shopify admin; the next daily calculation shows it as the live price.",
  // Stage and commit (release R4 minus C6)
  newPrice: "New price",
  onePrice: "one price for every ship country",
  floorNote: "the price floor is checked when you save",
  // The own-channel floor from the Config read (C1 v1.16); C2 still judges it on every save and commit.
  floorIs: (floor: string) => `floor ${floor}, a price below it needs a recorded reason`,
  // A floor in another currency than the row's price cannot be compared, so C2 asks a reason for every save (`floor_not_comparable`).
  floorOther: (floor: string, floorCurrency: string, currency: string) => `floor ${floor} (${floorCurrency}): every price in ${currency} needs a recorded reason`,
  noFloor: "no price floor set",
  marginBasis: "Margin basis",
  // T29 (audit P9): the drawer's figures are the list price; on another order type it says where that view's figures are.
  orderTypeOther: (view: string) => `list price · these figures are Single order; the ${view} figures are in the New price box above`,
  floorCell: (floor: string) => `floor ${floor}`,
  floorCellOther: (floor: string, floorCurrency: string) => `floor ${floor}, in ${floorCurrency}: every price here needs a reason`,
  floorLine: {
    unread: "Checked when you save",
    meta: "a price below it needs a recorded reason to save",
    otherMeta: (floorCurrency: string, currency: string) => `in ${floorCurrency}: every price in ${currency} needs a recorded reason to save`,
    none: "No floor set",
    noneMeta: "floors are set in Config",
    set: (source: string, who: string, date: string, rule: string) => `${source} · ${who}, ${date} · ${rule}`,
  },
  currencyConverted: (source: string) => `COGS converted into it at the ${source} rate`,
  // The margins at the typed price (C1 v1.11): C2 v1.13 `POST /api/price-run/project` answers them, the browser shows its strings.
  projRegion: "Margins at the new price",
  projEnter: "Enter a price to see the margins at it.",
  projPending: (price: string) => `Working out the margins at ${price}…`,
  projUnavailable: "We can't work out the margins at this price right now. Saving still works; the next daily calculation shows them.",
  projNotPricedHere: "This SKU is not priced here, so there are no margins to work out.",
  projAt: (price: string) => `At ${price}`,
  projNoCommitted: "No live price to compare with",
  projMargins: (gross: string, fulfil: string, fees: string) => `Gross ${gross} · Fulfil ${fulfil} · Fees ${fees}`,
  projRecomputedPrice: (rate: string) => `recomputed at ${rate} of the price`,
  projRecomputedFees: (rate: string) => `recomputed at ${rate} of commission plus FBA fee`,
  projMinimum: (minimum: string) => `the marketplace minimum of ${minimum} applies`,
  projStandard: (value: string) => `PFC standard ${value}`,
  projFixed: "fixed, as fed",
  projNoCost: "no cost yet",
  projTotal: (total: string) => `total ${total}`,
  projVs: (current: string) => `vs ${current.toLowerCase()}`,
  projVsAmazonUk: "vs Amazon UK",
  projAnchorRow: "this is the Amazon UK row",
  projVsRrp: "vs RRP",
  projConverted: (price: string, date: string) => `${price} at the ECB rate of ${date}`,
  projBelowRrp: (rrp: string) => `below the RRP of ${rrp}`,
  projAtOrAboveRrp: (rrp: string) => `at or above the RRP of ${rrp}`,
  projMissing: (reasons: string) => `Cannot be worked out: ${reasons}.`,
  projFooter: (basis: string, net: string | null) => `${basis} basis · VAT inclusive prices${net ? ` · price without VAT ${net}` : ""}`,
  lower: "Lower price",
  raise: "Raise price",
  reasonLabel: "Reason for a price below the floor",
  reasonPlaceholder: "Why is this price below the floor?",
  reasonLabelOther: "Reason for this price (the floor is in another currency)",
  reasonPlaceholderOther: "Why this price?",
  save: "Save",
  saving: "Saving…",
  saveOverride: "Save with override",
  saved: (price: string, withReason: boolean) =>
    `Saved ${price}, not live. It goes live when you commit the batch and apply it by hand.${withReason ? " Below-floor reason recorded." : ""}`,
  savedChip: (price: string) => `Saved ${price}, not live`,
  savedOverride: (reason: string) => `Reason saved with this price: ${reason}`,
  footnote: "Switch the order type, the margin basis and the date above. Each COGS measure and its date is in a row's Detail; they are never combined. A price below its floor needs a reason to save. A price below the region RRP is an Amazon parity risk where the RRP rule applies, and needs an exception to save. A flag carried from the fulfilment feed names the component it sits on.",
  history: {
    last: (n: number, prices: string) => `Last ${n} prices, oldest first: ${prices}`,
    chart: (n: number) => `The last ${n} prices as a line, oldest first`,
    unread: "The earlier prices could not be read from the Price log.",
  },
  remove: "Remove from the batch",
  removed: "Removed from the batch. The committed price stands.",
  enterPrice: "Enter a price above zero, with at most two decimals.",
  belowFloor: (floor: string) => `Below the floor of ${floor}. Give a reason to save.`,
  floorCurrency: (floor: string, floorCurrency: string, currency: string) =>
    `The floor is ${floor} ${floorCurrency}; this price is in ${currency}. Give a reason to save.`,
  unchanged: (price: string) => `${price} is already the committed price.`,
  noEdit: (areaName: string) => `You can view prices here. Ask the ${areaName} area owner for edit access to change them.`,
  commitRegion: "Saved prices",
  savedCount: (n: number, byMarket: Array<{ market: Market; n: number }>) =>
    `${n} ${n === 1 ? "price" : "prices"} saved, not live` +
    (byMarket.length > 1 ? ` · ${byMarket.map((m) => `${MARKET_LABEL[m.market]} ${m.n}`).join(", ")}` : ""),
  commitNote: "Committing writes them to the Price Log and gives you the list to apply by hand.",
  commitButton: (n: number) => `Commit ${n} ${n === 1 ? "price" : "prices"}`,
  nothingCommittedFloor: (n: number, lines: string) =>
    `Nothing committed. ${n} ${n === 1 ? "line is" : "lines are"} below a price floor that changed after ${n === 1 ? "it was" : "they were"} saved and ${n === 1 ? "has" : "have"} no reason recorded: ${lines}. Open each row, give a reason or change the price, then commit again.`,
  nothingCommittedUnchanged: (n: number, lines: string) =>
    `Nothing committed. ${n} ${n === 1 ? "line equals" : "lines equal"} the price committed since ${n === 1 ? "it was" : "they were"} saved: ${lines}. Remove or change ${n === 1 ? "it" : "them"}, then commit again.`,
  nothingToCommit: "Nothing to commit: no saved price is left in this batch.",
  // C1 v1.38 (C2 v1.41 `changed_since_review`): one sentence for both commits, the Price Run's and the Shipping pane's.
  changedSinceReview: (n: number, lines: string, what: "price" | "threshold") =>
    `Nothing committed. ${n} saved ${what}${n === 1 ? "" : "s"} changed while this review was open: ${lines}. The review now shows what is saved; check it, then commit again.`,
  reviewAdded: (what: string, value: string, who: string) => `${what} saved at ${value} by ${who}`,
  reviewResaved: (what: string, value: string, who: string) => `${what} saved again at ${value} by ${who}`,
  reviewRemoved: (what: string) => `${what} no longer saved`,
  reviewedPrice: "a price in this review",
  // A refusal's re-read can empty the batch while a review is open: it says so, and Commit is disabled.
  nothingLeft: "Nothing left to review",
  nothingSaved: "Nothing is saved now; close this review.",
  applyRegion: "Apply by hand",
  committedTitle: (n: number, when: string, who: string) => `Committed ${n} ${n === 1 ? "price" : "prices"} at ${when} (${who}).`,
  applyNote: "Apply them by hand in Seller Central and the Shopify admin. Tick each one as you go; the next calculation shows them here as the live price.",
  applied: "Applied",
  done: "Done",
  none: "none",
  overrideNote: (reason: string) => `below floor, reason: ${reason}`,
  committedLine: (price: string, date: string) => `Committed ${price} ${date} · live in the next calculation`,
  historyCommitted: (price: string, date: string, who: string, reason: string | null) =>
    `${price}, committed ${date} by ${who}, source Price Run${reason ? ` · override: ${reason}` : ""}.`,
  historyEarlier: "Earlier: from the legacy price sheet import.",
  showCountries: "Show countries",
  hideCountries: "Hide countries",
  detail: "Detail",
} as const;

const DATE = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
const TIME = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: "UTC" });
const REGION_NAME = new Intl.DisplayNames(["en-GB"], { type: "region" });

function asDate(iso: string): Date {
  return new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
}
/** Whether two timestamps name the same instant, whatever offset each carries (C2 serves the log's in UTC, a run's in the session zone). */
export function sameInstant(a: string, b: string): boolean {
  return asDate(a).getTime() === asDate(b).getTime();
}
/** "3 Mar 2026". A bare date is read as UTC midnight so it never shifts a day. */
export function humanDate(iso: string | null | undefined): string {
  return iso ? DATE.format(asDate(iso)) : "date unknown";
}
/** "18 Sept 2026 · 06:02 UTC", whatever offset the timestamp carries. */
export function stamp(iso: string | null | undefined): string {
  return iso ? `${DATE.format(asDate(iso))} · ${TIME.format(asDate(iso))} UTC` : "date unknown";
}
/** Whole days between a feed date and now: a date difference, never a money or percentage figure. */
export function daysSince(iso: string, now: Date): number {
  return Math.floor((now.getTime() - asDate(iso).getTime()) / 86_400_000);
}
export function latestIso(values: Array<string | null | undefined>): string | null {
  return values.filter((v): v is string => Boolean(v)).sort().at(-1) ?? null;
}
export function earliestIso(values: Array<string | null | undefined>): string | null {
  return values.filter((v): v is string => Boolean(v)).sort()[0] ?? null;
}
/** "DE" to "Germany". Falls back to the code for anything Intl does not know. */
export function countryName(code: string | null | undefined): string {
  if (!code) return ABSENT;
  try { return REGION_NAME.of(code) ?? code; } catch { return code; }
}
let COUNTRY_CODES: Map<string, string> | null = null;
/** A code that is its own current form: a retired one ("DD", "UK", "BU") is named as the country that replaced it ("Germany"),
 * so it must not answer for that name. */
function currentRegion(code: string): boolean {
  try { return new Intl.Locale(`und-${code}`).maximize().region === code && new Intl.Locale(`und-${code}`).region === code; } catch { return false; }
}
/** "Germany" to "DE": the two-letter code `countryName` names so, or null for any other text (C1 v1.36: the Price Run writes a
 * ship country's Marketplace filter by its name, and C2 reads a ship country by its code). Built once, on first use. */
export function countryCode(name: string | null | undefined): string | null {
  if (!name) return null;
  if (!COUNTRY_CODES) {
    COUNTRY_CODES = new Map();
    const A = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    for (const a of A) for (const b of A) {
      const code = a + b, named = countryName(code);
      if (named !== code && currentRegion(code) && !COUNTRY_CODES.has(named)) COUNTRY_CODES.set(named, code);
    }
  }
  return COUNTRY_CODES.get(name) ?? null;
}

export function money(value: string | null | undefined, currency: string | null | undefined): string {
  if (value == null || !currency) return ABSENT;
  return new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(Number(value));
}
export function pct(value: string | null | undefined): string {
  if (value == null) return ABSENT;
  const rounded = Math.round(Number(value)) || 0; // `|| 0` turns -0 (from -0.4) into 0, so the cell never reads "-0%"
  return `${new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 }).format(rounded)}%`;
}
/** "1 order", "52 orders". */
export function orders(n: number | null | undefined): string {
  const count = n ?? 0;
  return `${count} ${count === 1 ? "order" : "orders"}`;
}
export function pct1(value: string | null | undefined): string {
  return value == null ? ABSENT : `${value}%`;
}
/** A change in percent as C2 states it, with its sign: "+7.4%", "-3.7%", "0.0%" (also for "-0.0", as `pct` guards -0). A string test, never a number. */
export function signedPct(value: string | null | undefined): string {
  if (value == null) return ABSENT;
  if (value === "0.0" || value === "-0.0") return "0.0%";
  return value.startsWith("-") ? `${value}%` : `+${value}%`;
}
export function vatPct(rate: string | null | undefined): string {
  if (rate == null) return ABSENT;
  return new Intl.NumberFormat("en-GB", { style: "percent", maximumFractionDigits: 1 }).format(Number(rate));
}
/** Below the Config target; never true without a target (unset or not yet read), so nothing is flagged against a guess. */
export function belowTarget(grossPct: string | null | undefined, target: MarginTarget): boolean {
  return grossPct != null && target != null && Number(grossPct) < Number(target);
}
/** Text colour for a gross margin: amber below the Config target, green at or above, none when either is absent. */
export function grossTone(grossPct: string | null | undefined, target: MarginTarget): string {
  if (grossPct == null || target == null) return "";
  return belowTarget(grossPct, target) ? "text-ks-warn" : "text-ks-pass";
}

/** A row's full identity; channel location distinguishes two stores selling one SKU in one market. */
export function rowKey(row: GridRow): string {
  return `${row.xero_sku}|${row.grain}|${row.marketplace_id}|${row.location ?? ""}|${row.channel_location}`;
}
export function channelLabel(row: { channel: Channel }): string {
  return row.channel === "amazon" ? "Amazon" : "Shopify";
}
/** What a flag says beyond its name: the order count behind a thin or no-sales estimate, the date an old value dates from, else
 * the date the component was fed. A date difference or a count, never a money or percentage figure. */
function flagDetail(part: FulfilmentComponent): string | null {
  if ((part.flag === "THIN" || part.flag === "NO SALES") && part.order_count != null) return orders(part.order_count);
  if (part.flag === "STALE" && part.fed_on) return `since ${humanDate(part.fed_on)}`;
  return part.fed_on ? `fed ${humanDate(part.fed_on)}` : null;
}
/** Every flag on the row's fulfilment parts that means something to a reader, one per flag and detail, naming the components it
 * sits on in feed order (C1 v1.17: "Commission, Digital services fee: few orders · 9 orders", as v26 named the component). */
export function fulfilmentFlags(row: GridRow): Array<{ text: string; title: string }> {
  const groups = new Map<string, { label: { text: string; title: string }; detail: string | null; components: Component[] }>();
  for (const part of row.fulfilment?.components ?? []) {
    const label = FLAG_LABEL[part.flag];
    if (!label) continue;
    const detail = flagDetail(part);
    const key = `${part.flag}|${detail ?? ""}`;
    const group = groups.get(key) ?? { label, detail, components: [] };
    group.components.push(part.component);
    groups.set(key, group);
  }
  return [...groups.values()].map(({ label, detail, components }) => ({
    text: `${components.map((c) => COMPONENT_LABEL[c]).join(", ")}: ${label.text.toLowerCase()}${detail ? ` · ${detail}` : ""}`, title: label.title,
  }));
}
/** Where a price in force came from, in words, for any source but a Price Run commit (bug 4050402f-c6c9-81f4). */
const PRICE_ORIGIN: Record<string, string> = { legacy_sheet: "legacy sheet", threshold_run: "threshold run", override_return: "override return" };
/** The price in force as the drawer names it (bug 4050402f-c6c9-81f4): only a Price Run commit is "Committed"; any other source
 * is the live price with where and when it was set ("Live price £25.99 (legacy sheet, 18 Sep 2026)"), as the grid calls it Live.
 * `word` heads the projection's line for the same price. */
export function priceInForce(price: Price | null, amount: string | null): { line: string; word: string } {
  if (!price || amount === null) return { line: "Live price none yet", word: "Live price" };
  if (price.source === "price_run") return { line: `Committed price ${amount} (${humanDate(price.set_at)})`, word: "Committed" };
  const origin = Object.hasOwn(PRICE_ORIGIN, price.source) ? PRICE_ORIGIN[price.source] : price.source;
  return { line: `Live price ${amount} (${origin}, ${humanDate(price.set_at)})`, word: "Live price" };
}
/** The last commit per market on view (C2 v1.3 `last_committed`): who, when and how many prices, "none yet" for a market with no
 * committed run. One market names no market; several name each. */
export function lastCommitLine(markets: Market[], last: LastCommitted[] | undefined): string {
  const part = (market: Market) => {
    const lc = last?.find((x) => x.market === market);
    return lc ? COPY.lastCommit(personName(lc.committed_by), humanDate(lc.committed_at), lc.line_count) : COPY.noCommit;
  };
  if (markets.length === 1) return `${COPY.lastCommitLabel}: ${part(markets[0])}`;
  return `${COPY.lastCommitLabel} · ${markets.map((m) => `${MARKET_LABEL[m]}: ${part(m)}`).join(" · ")}`;
}
/** The FX source the daily calculation converted this row's COGS with (C4 carries the rate and its source on each converted
 * measure), or null when it converted none: the row's costs are in its own currency. */
export function costFx(row: GridRow): string | null {
  for (const m of [row.cogs, ...Object.values(row.cogs_measures ?? {})]) if (m && !("missing" in m) && m.fx_rate != null) return m.fx_source ?? "ECB";
  return null;
}
/** Whether the daily calculation found a COGS figure for this row on either measure. */
export function hasCost(row: GridRow): boolean {
  return [row.cogs, ...Object.values(row.cogs_measures ?? {})].some((m) => m != null && !("missing" in m));
}
/** Why a row has no COGS figure on its basis. C4 leaves `cogs` null for three different causes. */
export function cogsNullReason(row: GridRow): string {
  if (row.missing.includes("no_production_record")) return MISSING_LABEL.no_production_record;
  if (row.missing.includes("no_current_inventory_cost")) return MISSING_LABEL.no_current_inventory_cost;
  if (row.missing.includes("no_fx_rate")) return `${MISSING_LABEL.no_fx_rate} into ${row.currency ?? "this currency"}`;
  return "Not converted: this row has no price or fee currency yet";
}
export function storeNote(weighting: Weighting, marketLabel: string): string {
  const { countries_counted: counted, countries_total: total } = weighting;
  if (total === 0) return "No ship countries in the fulfilment feed yet";
  if (counted === 0) return "No ship country could be counted yet; each has something missing";
  if (total === 1) return "one ship country";
  const base = `${weighting.label} across ${marketLabel} countries`;
  return counted < total ? `${base} · ${counted} of ${total} countries counted, the rest have something missing` : base;
}
/** How the projection folded a Shopify store's ship countries (C2 v1.13 `weighting`): as the grid weights, by orders. */
export function projectionWeighting(w: ProjectionWeighting | null): string {
  if (w === null || w.countries_total === 0) return "No ship countries in the fulfilment feed yet";
  if (w.method === null) return "No ship country could be counted yet; each has something missing";
  if (w.method === "plain_mean") return `Plain average across ${w.countries_counted} ${w.countries_counted === 1 ? "country" : "countries"} (no orders counted yet)`;
  return `Sales-weighted by orders across ${w.countries_counted} of ${w.countries_total} ${w.countries_total === 1 ? "country" : "countries"}`;
}
/** "bojan@ketosource.co" to "Bojan": the audit trail stores emails, the screen shows a name. */
export function personName(email: string | null | undefined): string {
  if (!email) return "unknown";
  const local = email.split("@")[0];
  return local.charAt(0).toUpperCase() + local.slice(1);
}
export function detailTitle(row: GridRow): string {
  return `${row.product} ${row.variant} / ${channelLabel(row)}`;
}
export function detailPlace(row: GridRow, market: Market): string {
  if (row.channel === "amazon") return row.marketplace_name ?? row.marketplace_id;
  return `${STORE_LABEL[row.channel_location] ?? `Shopify ${MARKET_LABEL[market]}`}, ships to ${countryName(row.location)}`;
}

export function messageFor(error: unknown, areaName: string): string {
  if (error instanceof PricingError) {
    if (error.reason() === "not_configured") return COPY.notConfigured;
    if (isNoGrid(error)) return COPY.emptyBody;
    if (error.status === 401 || error.status === 403) return COPY.forbidden(areaName);
  }
  return COPY.unreachable;
}
